import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { ArtistProfile } from '../../types';
import './ArtistSelector.css';

interface ArtistSelectorProps {
  value?: number;
  onChange: (artistId: number | undefined) => void;
  placeholder?: string;
}

const ArtistSelector: React.FC<ArtistSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Search and select an artist...'
}) => {
  const [artists, setArtists] = useState<ArtistProfile[]>([]);
  const [filteredArtists, setFilteredArtists] = useState<ArtistProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedArtist, setSelectedArtist] = useState<ArtistProfile | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchArtists = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await api.get<ArtistProfile[]>('/admin/artists');
        setArtists(response.data);
        setFilteredArtists(response.data);
      } catch (error) {
        console.error('Failed to fetch artists:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtists();
  }, []);

  useEffect(() => {
    if (value && artists.length > 0) {
      const artist = artists.find(a => a.userId === value);
      setSelectedArtist(artist || null);
      if (artist) {
        setSearchQuery(artist.displayName || artist.email);
      }
    } else {
      setSelectedArtist(null);
      if (!isOpen) {
        setSearchQuery('');
      }
    }
  }, [value, artists, isOpen]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredArtists(artists);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = artists.filter(artist =>
        artist.displayName?.toLowerCase().includes(query) ||
        artist.email.toLowerCase().includes(query) ||
        artist.userId.toString().includes(query)
      );
      setFilteredArtists(filtered);
    }
  }, [searchQuery, artists]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (!selectedArtist) {
          setSearchQuery('');
        } else {
          setSearchQuery(selectedArtist.displayName || selectedArtist.email);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedArtist]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
    setIsOpen(true);
    if (!e.target.value) {
      setSelectedArtist(null);
      onChange(undefined);
    }
  };

  const handleSelectArtist = (artist: ArtistProfile): void => {
    setSelectedArtist(artist);
    setSearchQuery(artist.displayName || artist.email);
    onChange(artist.userId);
    setIsOpen(false);
  };

  const handleClear = (): void => {
    setSelectedArtist(null);
    setSearchQuery('');
    onChange(undefined);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleInputFocus = (): void => {
    setIsOpen(true);
  };

  return (
    <div className="artist-selector" ref={dropdownRef}>
      <div className="artist-selector-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="artist-selector-input"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          autoComplete="off"
          aria-label="Search artists"
        />
        {selectedArtist && (
          <button
            type="button"
            className="artist-selector-clear"
            onClick={handleClear}
            title="Clear selection"
            aria-label="Clear artist selection"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
        <button
          type="button"
          className="artist-selector-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Hide artist list' : 'Show artist list'}
          aria-expanded={isOpen}
        >
          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
        </button>
      </div>

      {isOpen && (
        <div className="artist-selector-dropdown">
          {loading ? (
            <div className="artist-selector-loading">Loading artists...</div>
          ) : filteredArtists.length === 0 ? (
            <div className="artist-selector-empty">No artists found</div>
          ) : (
            <div className="artist-selector-list" role="listbox" aria-label="Artists">
              {filteredArtists.map((artist) => (
                <div
                  key={artist.userId}
                  className={`artist-selector-item ${selectedArtist?.userId === artist.userId ? 'selected' : ''}`}
                  role="option"
                  aria-selected={selectedArtist?.userId === artist.userId}
                  tabIndex={0}
                  onClick={() => handleSelectArtist(artist)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectArtist(artist);
                    }
                  }}
                >
                  <div className="artist-selector-item-info">
                    <div className="artist-selector-item-name">{artist.displayName || artist.email}</div>
                    {artist.displayName && (
                      <div className="artist-selector-item-email">{artist.email}</div>
                    )}
                    <div className="artist-selector-item-id">User ID: {artist.userId}</div>
                  </div>
                  {selectedArtist?.userId === artist.userId && (
                    <div className="artist-selector-item-check">
                      <i className="fas fa-check"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArtistSelector;
